import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import invoiceIcon from '@/assets/images/svg/invoice_icon.svg';
import downloadIconWhite from '@/assets/images/svg/download_icon.svg';

interface PeriodOption {
  year: number;
  month: number;
  label: string;
}

interface InvoicesHeaderProps {
  periodLabel: string;
  onPeriodChange: (year: number, month: number) => void;
  onDownloadAll: () => void;
  canDownload: boolean;
  hideTitle?: boolean;
}

export const InvoicesHeader: React.FC<InvoicesHeaderProps> = ({
  periodLabel,
  onPeriodChange,
  onDownloadAll,
  canDownload,
  hideTitle = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const periods = React.useMemo<PeriodOption[]>(() => {
    const now = new Date();
    return Array.from({ length: 12 }, (_, i) => {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      };
    });
  }, []);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: PeriodOption) => {
    onPeriodChange(option.year, option.month);
    setIsOpen(false);
  };

  return (
    <div className='mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-center'>
      {/* Left Group: Title + Period */}
      <div className='flex flex-col gap-6 sm:flex-row sm:items-center md:gap-12'>
        {!hideTitle && (
          <div className='flex shrink-0 items-center gap-2'>
            <img src={invoiceIcon} alt='' className='size-6' />
            <h2 className='font-sans text-20 font-medium leading-27 text-dark-100'>Invoices</h2>
          </div>
        )}

        {/* Period Selector */}
        <div className='relative w-full sm:w-auto' ref={dropdownRef}>
          <Button
            variant='settings-modify'
            className='h-[41px] w-full items-center justify-between px-5 sm:min-w-[240px]'
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className='flex items-center gap-1.5 text-16 leading-27 text-dark-300'>
              <span className='font-sans font-normal'>Period:</span>
              <span className='font-sans font-bold'>{periodLabel}</span>
            </div>
            {isOpen ? (
              <ChevronDownIcon className='size-4 stroke-[2] text-dark-300' />
            ) : (
              <ChevronRightIcon className='size-4 stroke-[2] text-dark-300' />
            )}
          </Button>

          {isOpen && (
            <div className='absolute left-0 top-full z-10 mt-2 w-full min-w-[240px] overflow-hidden rounded-10 border border-gray-200 bg-white shadow-xl'>
              <div className='flex flex-col py-1'>
                {periods.map((option) => (
                  <button
                    key={option.label}
                    onClick={() => handleSelect(option)}
                    className={clsx(
                      'px-4 py-2 text-left font-sans text-14 transition-colors',
                      option.label === periodLabel
                        ? 'bg-brand-50 font-bold text-brand-800'
                        : 'font-normal text-gray-600 hover:bg-gray-50 hover:text-brand-800'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right: Download All */}
      <div className='w-full md:w-auto'>
        <Button
          variant='payments-download-all'
          className='h-[41px] w-full justify-center gap-2 md:w-[169px]'
          onClick={onDownloadAll}
          disabled={!canDownload}
        >
          <img src={downloadIconWhite} alt='' className='size-6 brightness-0 invert' />
          Download All
        </Button>
      </div>
    </div>
  );
};
